import React, { ElementType } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { colors, media } from '../../../../App/ChakraTheme';
import {
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  Progress,
  Text,
  VStack,
  useColorMode,
} from '@chakra-ui/react';
import styled, { css } from '../../../../consts/styled';
import { Thumbnails } from '../../hooks/api/types';
import DotInfo from '../../../../components/DotInfo';
import useTutorialInfo, { InfoStep } from '../../../../hooks/useTutorialInfo';
import { useIsMobile } from '../../../../hooks/isMobile';
import { useIsTablet } from '../../../../hooks/isTablet';

interface ShareButtonProps extends BoxProps {
  share: {
    canBeShared: number;
    onShare: () => any;
    text: string;
    disabled: boolean;
  };
  color?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ share, color, ...rest }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  
  return (
    <Box
      w="full"
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-end' : 'center'}
      justifyContent="space-between"
      gap={2}
      {...rest}
    >
      <Text as="p" textStyle="h5" color="#FFF" textAlign={isMobile ? 'right' : 'left'}>
        {share.text}
      </Text>
      {share.canBeShared !== 1 && share.canBeShared !== -1 && (
        <Tippy
          arrow={true}
          delay={800}
          theme="light"
          animation="scale"
          content={t('classComponent.shareTippy')}
        >
          <Button
            colorScheme={color || 'blue'}
            color={isDark ? "white" : undefined}
            className="small"
            style={{
              width: 'fit-content',
              minWidth: isMobile || isTablet ? '100px' : '126px',
              maxWidth: isMobile || isTablet ? '160px' : '200px',
              flexShrink: 1,
              justifyContent: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              border: isDark ? "2px solid rgba(255,255,255,0.85)" : "1px solid rgba(255,255,255,0.6)",
              fontSize: isMobile || isTablet ? '11px' : '12px',
              padding: isMobile || isTablet ? '4px 6px' : '6px 8px',
              lineHeight: '1.1',
            }}
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '2px' }}
            disabled={share.disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              e.preventDefault();
              share.onShare();
            }}
          >
            {t('classComponent.shareAll')}
          </Button>
        </Tippy>
      )}
    </Box>
  );
};

const LinkToBeWrapped: React.FC<LinkProps & { isEnabled: boolean }> = ({
  isEnabled,
  ...rest
}) => {
  if (isEnabled) {
    return <Link {...rest} />;
  }

  return (
    <div
      style={{
        cursor: isEnabled ? 'pointer' : 'auto',
        pointerEvents: isEnabled ? 'visible' : 'none',
      }}
      {...(rest as any)}
    />
  );
};

const ClassComponentWrapper = styled(LinkToBeWrapped)`
  border-radius: 4px;
  transition: box-shadow 0.3s ease-out, transform 0.3s ease-out,
    background-color 0.3s ease-out;
  transition-delay: 0.1s;
  transform: translateY(0);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  display: block;

  &:focus-visible {
    outline: 3px solid var(--stemi-focus-color);
    outline-offset: 3px;
    border-radius: 4px;
  }

  .card-analytics-link:focus-visible {
    outline: 3px solid var(--stemi-focus-color);
    outline-offset: 3px;
    border-radius: 4px;
  }

  ${(p) => {
    if (p.isEnabled) {
      return css`
        &:hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.15);
          background-color: ${colors.applyOpacity(colors.mischka, 0.2)};
          transform: translateY(-2px);
        }
      `;
    }

    return css`
      opacity: 0.4;
      cursor: not-allowed;
    `;
  }}
  .content {
    padding: 24px;
    ${media.phone`padding: 16px;`};
  }

  img {
    object-fit: cover;
    height: 100%;
    width: 100%;
  }

  img:before {
    content: ' ';
    display: block;
    position: absolute;
  }

  .card-class-image {
    flex-shrink: 0;
    width: 75px;
    height: 75px;
    margin-right: 16px;
    border-radius: 4px;
    overflow: hidden;
    ${media.phone`
  width: 100px;
  height: 100px;
  margin-right: 0;
`};
  }

  .new-items {
    position: absolute;
    background: red;
    border-radius: 100px;
    height: 24px;
    width: 24px;
    right: 4px;
    top: 4px;
    color: #fff;
    justify-content: center;
    align-items: center;
    text-align: center;
    display: flex;
    font-weight: bold;
    font-size: 12px;
  }
`;

interface ClassComponentProps {
  to: string;
  name?: string;
  description?: string;
  progress?: number | null;
  share?: ShareButtonProps['share'];
  thumbnails?: Thumbnails;
  ImageComponent?: ElementType;

  canBeShared?: number | null;

  id?: string;
  classId?: string;
  programId?: string;
  isTeacherOrStaff?: boolean;
  image?: any;

  isPublished?: boolean;
  isStaff?: boolean;
  isSelfLearning?: boolean;

  sameHeight?: boolean;
  sameWidth?: boolean;
  enabled?: boolean;
  isUnit?: boolean;
  showInfo?: boolean;
  newItems?: number;
  color?: string;
  target?: string;
  isChallenge?: boolean;
  edit?: boolean;
  isPublic?: boolean;
}

const ClassComponentNew: React.FC<ClassComponentProps> = (props) => {
  const {
    to,
    name,
    description,
    thumbnails,
    // image,
    color,
    // ImageComponent,
    progress,
    id,
    classId,
    isTeacherOrStaff,
    canBeShared,
    isStaff,
    isPublished,
    programId,
    isSelfLearning,
    sameHeight = true,
    sameWidth = true,
    share = undefined,
    isUnit,
    showInfo,
    newItems,
    target,
    edit,
    isChallenge,
    isPublic,
  } = props;
  let { enabled = true } = props;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const { currentStep, nextStep } = useTutorialInfo();
  const isInfoShown = showInfo && currentStep === InfoStep.OPEN_CLASS;

  const sizeStyle = { height: 'auto', width: '' };
  if (sameHeight) {
    sizeStyle.height = '100%';
  }
  if (sameWidth) {
    sizeStyle.width = '100%';
  }

  if (isUnit) {
    if (
      !isTeacherOrStaff &&
      (props.canBeShared == null || canBeShared === -1 || canBeShared === 0)
    ) {
      enabled = !!isSelfLearning || !!isPublic;
    }

    if (!isStaff && !isPublished) {
      enabled = false;
    }
  }

  return (
    <ClassComponentWrapper
      to={to}
      target={target || '_self'}
      onClick={() => {
        nextStep();
      }}
      style={{
        ...sizeStyle,
        borderRadius: '25px',
        overflow: 'hidden',
        background: color,
        paddingBottom: '36px',
        maxWidth: '400px',
      }}
      isEnabled={enabled}
      data-cy="dashboard-class"
    >
      {newItems ? (
        <div className="new-items">
          <span>{Math.min(newItems, 9)}</span>
        </div>
      ) : null}
      <Flex flexDir="column" w="full" h="full">
        <img
          src={thumbnails?.['754x425'] || 'https://picsum.photos/754/425'}
          alt={name ?? 'class image'}
          style={{ height: 'auto', flexBasis: '100%', flex: 1 }}
        />
        <VStack
          spacing={0}
          gap={4}
          align="start"
          justify="space-between"
          flexBasis={'100%'}
          paddingLeft={isMobile ? '16px' : '34px'}
          paddingRight={isMobile ? '16px' : '34px'}
          paddingTop={isMobile ? '16px' : '22px'}
        >
          <div>
            {name ? (
              <Text
                fontSize={isMobile ? '20px' : '30px'}
                lineHeight={isMobile ? '24px' : '32px'}
                fontWeight="600"
                color="#FFF"
              >
                {name}
              </Text>
            ) : null}
            {description ? (
              <Text
                as="div"
                fontSize={isMobile ? '12px' : '14px'}
                lineHeight={isMobile ? '16px' : '16px'}
                fontWeight="300"
                mt="10px"
                color="#FFF"
              >
                {description}
              </Text>
            ) : null}
          </div>
          <VStack justify="space-between" spacing={0} gap={2} w="full">
            {progress ? (
              <Progress
                hasStripe
                value={progress || 1}
                h="10px"
                w="100%"
                mt={3}
                mb={3}
                background="white"
                borderRadius="30px"
                fill={color}
                aria-label={`Unit progress: ${Math.round(progress || 1)}%`}
              />
            ) : null}

            {share && enabled && <ShareButton share={share} color={color} />}
            {!isChallenge && !edit && isTeacherOrStaff && enabled && (
              <HStack justify="space-between" w="full">
                <Button
                  w={isMobile ? '100px' : '126px'}
                  variant="solid"
                  color={isDark ? "white" : color}
                  fontSize={isMobile ? '12px' : '14px'}
                  padding={isMobile ? '8px 16px' : '12px 24px'}
                  border={isDark ? "2px solid rgba(255,255,255,0.85)" : "2px solid rgba(255,255,255,0.6)"}
                  tabIndex={-1}
                >
                  {t('classComponent.getStarted')}
                </Button>
                <Tippy
                  arrow={true}
                  delay={800}
                  theme="light"
                  animation="scale"
                  content={t('classComponent.analyticsTippy')}
                >
                  <Link
                    data-cy-analytics
                    className="card-analytics-link"
                    to={`/program/${programId}/analytics/?analytics-unit-id=${id}&study-group-id=${classId}`}
                  >
                    <Button
                      w={isMobile ? '100px' : '126px'}
                      variant="outline"
                      color="white"
                      fontSize={isMobile ? '12px' : '14px'}
                      padding={isMobile ? '8px 16px' : '12px 24px'}
                      border={isDark ? "2px solid rgba(255,255,255,0.85)" : "2px solid rgba(255,255,255,0.6)"}
                      tabIndex={-1}
                    >
                      {t('classComponent.analytics')}
                    </Button>
                  </Link>
                </Tippy>
              </HStack>
            )}
            {!isTeacherOrStaff && enabled && (
              <HStack justify="space-between" w="full">
                <Button
                  w={isMobile ? '100px' : '126px'}
                  variant="solid"
                  color={isDark ? "white" : color}
                  fontSize={isMobile ? '12px' : '14px'}
                  padding={isMobile ? '8px 16px' : '12px 24px'}
                  border={isDark ? "2px solid rgba(255,255,255,0.85)" : "2px solid rgba(255,255,255,0.6)"}
                  tabIndex={-1}
                >
                  {t('classComponent.getStarted')}
                </Button>
              </HStack>
            )}
            {isInfoShown ? (
              <DotInfo
                info="Open class"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              />
            ) : null}
          </VStack>
        </VStack>
      </Flex>
    </ClassComponentWrapper>
  );
};

export default ClassComponentNew;
